import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { toast } from "react-toastify";
import { LoaderPinwheel, X } from "lucide-react";
import { hasPermission } from "../../utils/permissions";
import PageSizeSelect from "../../components/ui/PageSizeSelect";

const API = process.env.REACT_APP_API_URL;
const token = () => localStorage.getItem("token");
const headers = () => ({ Authorization: `Bearer ${token()}` });

const EMPTY_FORM = { name: "", sedes: [] };

const SedeGruposPage = () => {
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form / modal
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // null = create
  const [form, setForm] = useState(EMPTY_FORM);

  // Selectors inside form
  const [clientes, setClientes] = useState([]);
  const [clienteSeleccionado, setClienteSeleccionado] = useState("");
  const [sedesPorCliente, setSedesPorCliente] = useState([]);
  const [loadingSedes, setLoadingSedes] = useState(false);

  // Paginación
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  /* ────────── fetch ────────── */
  const fetchGrupos = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/sede-grupos`, { headers: headers() });
      setGrupos(res.data);
      setPage(1);
    } catch {
      toast.error("Error cargando grupos de sedes");
    } finally {
      setLoading(false);
    }
  };

  const fetchClientes = async () => {
    try {
      const res = await axios.get(`${API}/clientes`, { headers: headers() });
      setClientes(res.data || []);
    } catch {
      toast.error("Error cargando clientes");
    }
  };

  const fetchSedesByCliente = async (clienteId) => {
    if (!clienteId) { setSedesPorCliente([]); return; }
    setLoadingSedes(true);
    try {
      const res = await axios.get(`${API}/sedes/${clienteId}`, { headers: headers() });
      setSedesPorCliente(res.data || []);
    } catch {
      toast.error("Error cargando sedes");
      setSedesPorCliente([]);
    } finally {
      setLoadingSedes(false);
    }
  };

  useEffect(() => { fetchGrupos(); fetchClientes(); }, []); // eslint-disable-line

  /* ────────── form helpers ────────── */
  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setClienteSeleccionado("");
    setSedesPorCliente([]);
    setShowForm(true);
  };

  const openEdit = (g) => {
    setEditing(g);
    setForm({ name: g.name, sedes: g.sedes.map((s) => s._id) });
    setClienteSeleccionado("");
    setSedesPorCliente([]);
    setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setEditing(null); };

  const toggleSede = (sedeId) => {
    setForm((f) => ({
      ...f,
      sedes: f.sedes.includes(sedeId)
        ? f.sedes.filter((id) => id !== sedeId)
        : [...f.sedes, sedeId],
    }));
  };

  const removeSede = (sedeId) => {
    setForm((f) => ({ ...f, sedes: f.sedes.filter((id) => id !== sedeId) }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error("El nombre es obligatorio");
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/sede-grupos/${editing._id}`, form, { headers: headers() });
        toast.success("Grupo actualizado");
      } else {
        await axios.post(`${API}/sede-grupos`, form, { headers: headers() });
        toast.success("Grupo creado");
      }
      closeForm();
      fetchGrupos();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error guardando grupo");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este grupo de sedes?")) return;
    try {
      await axios.delete(`${API}/sede-grupos/${id}`, { headers: headers() });
      fetchGrupos();
    } catch {
      toast.error("No se pudo eliminar el grupo");
    }
  };

  /* ────────── selecciones dentro del form ────────── */
  // Sedes ya seleccionadas enriquecidas con nombre (de los grupos cargados o de sedesPorCliente)
  const allKnownSedes = useMemo(() => {
    const map = new Map();
    grupos.forEach((g) => g.sedes.forEach((s) => map.set(s._id, s)));
    sedesPorCliente.forEach((s) => map.set(s._id, { _id: s._id, name: s.name, cliente: { name: clientes.find((c) => c._id === clienteSeleccionado)?.name } }));
    return map;
  }, [grupos, sedesPorCliente, clientes, clienteSeleccionado]);

  /* ────────── paginación ────────── */
  const totalPages = Math.max(1, Math.ceil(grupos.length / pageSize));
  const paged = grupos.slice((page - 1) * pageSize, page * pageSize);

  /* ────────── render ────────── */
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Grupos de sedes</h2>
        {hasPermission("clientes.ver") && (
          <Button onClick={openCreate}>+ Nuevo grupo</Button>
        )}
      </div>

      {loading && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-700">
          <LoaderPinwheel className="h-4 w-4 animate-spin text-blue-600" />
          Cargando datos...
        </div>
      )}

      <div className="flex items-center justify-end mb-2">
        <PageSizeSelect
          value={pageSize}
          onChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full bg-white rounded-lg shadow-sm">
          <thead className="bg-gray-100 text-gray-700 text-sm uppercase">
            <tr>
              <th className="py-2 px-4 text-left">Nombre</th>
              <th className="py-2 px-4 text-left">Sedes</th>
              <th className="py-2 px-4 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {paged.length ? (
              paged.map((g) => (
                <tr key={g._id} className="border-b hover:bg-gray-50">
                  <td className="py-2 px-4 font-medium">{g.name}</td>
                  <td className="py-2 px-4 text-sm text-gray-600">
                    {g.sedes.length === 0 ? (
                      <span className="italic text-gray-400">Sin sedes</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {g.sedes.map((s) => (
                          <span key={s._id} className="bg-blue-50 text-blue-700 text-xs px-2 py-0.5 rounded-full">
                            {s.cliente?.name ? `${s.cliente.name} · ` : ""}{s.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="py-2 px-4 text-right space-x-2">
                    <Button size="sm" variant="outline" onClick={() => openEdit(g)}>
                      Editar
                    </Button>
                    <Button size="sm" className="bg-red-500 text-white hover:bg-red-600" onClick={() => handleDelete(g._id)}>
                      Eliminar
                    </Button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="3" className="text-center py-4 text-gray-500">
                  No hay grupos creados
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex justify-between items-center mt-4 text-sm text-gray-600">
          <span>Mostrando {paged.length} de {grupos.length}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>← Anterior</Button>
            <span className="px-2 py-1 bg-gray-100 rounded-md">Página {page} de {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>Siguiente →</Button>
          </div>
        </div>
      )}

      {/* ── Modal ── */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-base font-semibold">
                {editing ? "Editar grupo de sedes" : "Nuevo grupo de sedes"}
              </h3>
              <button onClick={closeForm} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="overflow-y-auto px-6 py-4 space-y-4 flex-1">
              {/* Nombre */}
              <div>
                <label className="block text-sm font-medium mb-1">Nombre del grupo</label>
                <input
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-400 focus:outline-none"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Ej: Hospitales Zona Norte"
                />
              </div>

              {/* Sedes seleccionadas */}
              {form.sedes.length > 0 && (
                <div>
                  <label className="block text-sm font-medium mb-1">Sedes en el grupo ({form.sedes.length})</label>
                  <div className="flex flex-wrap gap-1">
                    {form.sedes.map((sid) => {
                      const s = allKnownSedes.get(sid);
                      return (
                        <span key={sid} className="flex items-center gap-1 bg-blue-50 text-blue-700 text-xs px-2 py-0.5 rounded-full">
                          {s ? `${s.cliente?.name ? s.cliente.name + " · " : ""}${s.name}` : sid}
                          <button onClick={() => removeSede(sid)} className="hover:text-red-500">
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Agregar sedes por cliente */}
              <div className="border rounded-lg p-3 bg-gray-50">
                <label className="block text-sm font-medium mb-2">Agregar sedes</label>
                <select
                  className="w-full border rounded-lg px-3 py-2 mb-2 focus:ring-2 focus:ring-blue-400 focus:outline-none"
                  value={clienteSeleccionado}
                  onChange={(e) => {
                    setClienteSeleccionado(e.target.value);
                    fetchSedesByCliente(e.target.value);
                  }}
                >
                  <option value="">Seleccionar cliente...</option>
                  {clientes.map((c) => (
                    <option key={c._id} value={c._id}>{c.name}</option>
                  ))}
                </select>

                {loadingSedes && (
                  <div className="flex items-center gap-2 text-sm text-blue-600">
                    <LoaderPinwheel className="h-3 w-3 animate-spin" /> Cargando sedes...
                  </div>
                )}

                {sedesPorCliente.length > 0 && (
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {sedesPorCliente.map((s) => {
                      const selected = form.sedes.includes(s._id);
                      return (
                        <label key={s._id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-white rounded px-2 py-1">
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => toggleSede(s._id)}
                            className="accent-blue-600"
                          />
                          {s.name}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="px-6 py-4 border-t flex justify-end gap-2">
              <Button variant="outline" onClick={closeForm}>Cancelar</Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? "Guardando..." : editing ? "Guardar cambios" : "Crear grupo"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SedeGruposPage;
